/**
 * VersioningTab — API 버전 관리 (C025) 라이브 데모
 * GET /api/eraf/v1/platform-lab/echo (Deprecation 헤더 캡처)
 * GET /api/eraf/v2/platform-lab/echo (확장 스키마)
 * 5상태: default / loading / empty / error / dialog(inline Callout)
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EmptyState, ErrorState } from '@ui-kit';
import { useEchoV1, useEchoV2 } from '../hooks/usePlatformLab';
import { versioningSchema } from '../schema/platformLab.schema';
import type { VersioningFormValues } from '../schema/platformLab.schema';
import type { EchoV1Response, EchoV2Response, EchoV1Headers, ApiError } from '../types/platformLab';

function JsonBlock({ value }: { value: unknown }) {
  return (
    <pre
      style={{
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--spacing-3)',
        fontFamily: 'var(--font-family-mono)',
        fontSize: 'var(--font-size-xs)',
        overflowX: 'auto',
        margin: 0,
        color: 'var(--color-text-primary)',
        lineHeight: 1.6,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function HeaderRow({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', marginBottom: 'var(--spacing-1)' }}>
      <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', minWidth: 120 }}>
        {label}:
      </span>
      <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)', wordBreak: 'break-all' }}>
        {value}
      </span>
    </div>
  );
}

export function VersioningTab() {
  const { t } = useTranslation();
  const echoV1Mutation = useEchoV1();
  const echoV2Mutation = useEchoV2();

  const [v1Data, setV1Data] = useState<EchoV1Response | null>(null);
  const [v1Headers, setV1Headers] = useState<EchoV1Headers | null>(null);
  const [v2Data, setV2Data] = useState<EchoV2Response | null>(null);
  const [v1Error, setV1Error] = useState<ApiError | null>(null);
  const [v2Error, setV2Error] = useState<ApiError | null>(null);
  const [deprecationDismissed, setDeprecationDismissed] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<VersioningFormValues>({
    resolver: zodResolver(versioningSchema),
    defaultValues: { msg: 'hello' },
  });

  const onCallV1 = handleSubmit((values) => {
    setV1Error(null);
    setDeprecationDismissed(false);
    echoV1Mutation.mutate(values.msg, {
      onSuccess: (result) => {
        setV1Data(result.data);
        setV1Headers(result.headers);
      },
      onError: (err) => {
        setV1Error(err as unknown as ApiError);
      },
    });
  });

  const onCallV2 = handleSubmit((values) => {
    setV2Error(null);
    echoV2Mutation.mutate(values.msg, {
      onSuccess: (result) => {
        setV2Data(result as unknown as EchoV2Response);
      },
      onError: (err) => {
        setV2Error(err as unknown as ApiError);
      },
    });
  });

  const hasAnyResult = v1Data !== null || v2Data !== null;
  const isLoading = echoV1Mutation.isPending || echoV2Mutation.isPending;

  // Deprecation Callout: show when v1 responded + has headers
  const showDeprecationCallout =
    v1Data !== null &&
    (v1Headers?.deprecation || v1Headers?.sunset) &&
    !deprecationDismissed;

  return (
    <div>
      <h2 style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, marginBottom: 'var(--spacing-4)', color: 'var(--color-text-primary)' }}>
        {t('platformLab.versioning.title')}
      </h2>

      {/* 입력 폼 */}
      <form
        style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)', alignItems: 'flex-end', marginBottom: 'var(--spacing-5)' }}
        onSubmit={(e) => e.preventDefault()}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', flex: '1 1 200px' }}>
          {t('platformLab.versioning.input.msg')}
          <input
            {...register('msg')}
            type="text"
            aria-label={t('platformLab.versioning.input.msg')}
            aria-invalid={!!errors.msg}
            aria-describedby={errors.msg ? 'msg-error' : undefined}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: `1px solid ${errors.msg ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family-sans)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
            }}
          />
          {errors.msg && <span id="msg-error" style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>{errors.msg.message}</span>}
        </label>

        <button
          type="button"
          onClick={onCallV1}
          disabled={isLoading}
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: 'var(--color-surface-elevated)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-family-sans)',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {echoV1Mutation.isPending ? '...' : t('platformLab.versioning.button.callV1')}
        </button>

        <button
          type="button"
          onClick={onCallV2}
          disabled={isLoading}
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: 'var(--color-brand-primary)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-family-sans)',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {echoV2Mutation.isPending ? '...' : t('platformLab.versioning.button.callV2')}
        </button>
      </form>

      {/* loading */}
      {isLoading && (
        <div
          aria-busy="true"
          aria-label="loading"
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', padding: 'var(--spacing-4)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 16,
              height: 16,
              borderRadius: '50%',
              border: '2px solid var(--color-brand-primary)',
              borderTopColor: 'transparent',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          Calling...
        </div>
      )}

      {/* empty — 아직 호출 전 */}
      {!isLoading && !hasAnyResult && !v1Error && !v2Error && (
        <EmptyState
          title={t('platformLab.versioning.empty.title')}
          description="v1/v2 버튼을 클릭하면 실시간으로 응답과 헤더를 비교할 수 있습니다"
        />
      )}

      {/* Deprecation Callout — dialog(inline) 상태 */}
      {showDeprecationCallout && (
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            background: 'var(--color-warning-subtle, #fff9e6)',
            border: '1px solid var(--color-warning, #f0ad4e)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--spacing-4)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-primary)',
          }}
        >
          <span style={{ fontSize: 'var(--font-size-base)' }}>⚠</span>
          <div style={{ flex: 1 }}>
            <strong>
              {t('platformLab.versioning.deprecation.notice', {
                sunset: v1Headers?.sunset ?? '2026-12-31',
              })}
            </strong>
            {v1Headers?.link && (
              <span style={{ marginLeft: 'var(--spacing-2)' }}>
                <a
                  href={v1Headers.link.replace(/<([^>]+)>.*/, '$1')}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--color-brand-primary)', fontWeight: 600 }}
                >
                  {t('platformLab.versioning.deprecation.link')}
                </a>
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setDeprecationDismissed(true)}
            aria-label="닫기"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-base)', color: 'var(--color-text-muted)', padding: 0 }}
          >
            ×
          </button>
        </div>
      )}

      {/* 결과 그리드 — v1 / v2 나란히 */}
      {(hasAnyResult || v1Error || v2Error) && !isLoading && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 'var(--spacing-4)',
          }}
        >
          {/* v1 결과 */}
          <div>
            <h3 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, marginBottom: 'var(--spacing-2)', color: 'var(--color-text-secondary)' }}>
              {t('platformLab.versioning.label.v1')}
            </h3>

            {/* error 상태 */}
            {v1Error && (
              <ErrorState
                title={t(`platformLab.error.${v1Error.code}`, { defaultValue: t('platformLab.error.network') })}
                message={v1Error.message}
                onRetry={() => { setV1Error(null); }}
              />
            )}

            {/* default 상태 */}
            {v1Data && !v1Error && (
              <>
                <JsonBlock value={v1Data} />
                {(v1Headers?.deprecation || v1Headers?.sunset || v1Headers?.link) && (
                  <div style={{ marginTop: 'var(--spacing-3)' }}>
                    <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-2)' }}>
                      {t('platformLab.versioning.label.headers')}
                    </p>
                    <div
                      style={{
                        background: 'var(--color-surface-muted)',
                        border: '1px solid var(--color-surface-border)',
                        borderRadius: 'var(--radius-sm)',
                        padding: 'var(--spacing-3)',
                      }}
                    >
                      <HeaderRow label="Deprecation" value={v1Headers?.deprecation} />
                      <HeaderRow label="Sunset" value={v1Headers?.sunset} />
                      <HeaderRow label="Link" value={v1Headers?.link} />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* v2 결과 */}
          <div>
            <h3 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, marginBottom: 'var(--spacing-2)', color: 'var(--color-text-secondary)' }}>
              {t('platformLab.versioning.label.v2')}
            </h3>

            {/* error 상태 */}
            {v2Error && (
              <ErrorState
                title={t(`platformLab.error.${v2Error.code}`, { defaultValue: t('platformLab.error.network') })}
                message={v2Error.message}
                onRetry={() => { setV2Error(null); }}
              />
            )}

            {/* default 상태 */}
            {v2Data && !v2Error && (
              <>
                <JsonBlock value={v2Data} />
                <div style={{ marginTop: 'var(--spacing-2)', padding: 'var(--spacing-2) var(--spacing-3)', background: 'var(--color-surface-muted)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                  + 필드 추가: <code style={{ fontFamily: 'var(--font-family-mono)' }}>echoedAt</code>, <code style={{ fontFamily: 'var(--font-family-mono)' }}>traceId</code>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
